import { useEffect, useRef } from 'react';
import propTypes from 'prop-types';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import post from '../../utils/fetchRequests/post';
import { backendUrl } from '../../configFiles/config';

/**
 * Reports join/leave to interview backend for meetingAttendance.
 * Needed when LiveKit webhooks cannot reach a local dev server.
 */
const AttendanceTracker = ({ meetingToken }) => {
  const room = useRoomContext();
  const hasReportedJoinRef = useRef(false);

  useEffect(() => {
    if (!room || !meetingToken) {
      return undefined;
    }

    const reportJoin = () => {
      if (hasReportedJoinRef.current) {
        return;
      }
      hasReportedJoinRef.current = true;
      post(`${backendUrl}/api/v1/meeting/attendance/join`, {}, {
        authorization: meetingToken,
      }).catch(() => {});
    };

    const reportLeave = (useKeepalive = false) => {
      if (!hasReportedJoinRef.current) {
        return;
      }
      hasReportedJoinRef.current = false;
      if (useKeepalive) {
        fetch(`${backendUrl}/api/v1/meeting/attendance/leave`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: meetingToken,
          },
          body: '{}',
          keepalive: true,
        }).catch(() => {});
      } else {
        post(`${backendUrl}/api/v1/meeting/attendance/leave`, {}, {
          authorization: meetingToken,
        }).catch(() => {});
      }
    };

    const handleConnected = () => {
      reportJoin();
    };

    if (room.state === 'connected') {
      reportJoin();
    }

    room.on(RoomEvent.Connected, handleConnected);

    const handlePageExit = () => {
      reportLeave(true);
    };
    window.addEventListener('beforeunload', handlePageExit);
    window.addEventListener('pagehide', handlePageExit);

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
      window.removeEventListener('beforeunload', handlePageExit);
      window.removeEventListener('pagehide', handlePageExit);
    };
  }, [room, meetingToken]);

  return null;
};

AttendanceTracker.propTypes = {
  meetingToken: propTypes.string.isRequired,
};

export default AttendanceTracker;
